/**
 * B7. Админ-панель: доступна только при is_admin.
 * Разделы: статистика, пользователи (с карточкой и выдачей доступа), тарифы, домены.
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi } from "../../api/endpoints";
import type {
  About,
  AdminDomainItem,
  Zone,
  AdminStats,
  AdminUserDetail,
  AdminUserListItem,
  Tariff,
  TariffDuration,
  TariffKind,
} from "../../api/types";
import { GramAmount } from "../../components/GramIcon";
import {
  Badge,
  Button,
  Empty,
  Field,
  Input,
  Loading,
  Notice,
  Segmented,
  Select,
  Sheet,
  Skeletons,
  Textarea,
} from "../../components/ui";
import { useTonPayment } from "../payments/useTonPayment";
import { useAppStore } from "../../store/app";
import { showConfirm } from "../../telegram/webapp";

type Tab = "stats" | "users" | "tariffs" | "domains" | "zone" | "about";

const DURATIONS: TariffDuration[] = ["month", "3month", "6month", "12month", "forever"];
// custom_code — не подписка, а разовая покупка возможности своего кода.
// Строка одна, и админ меняет в ней цену: этого и просил заказчик.
const KINDS: TariffKind[] = ["base", "pro", "custom_code"];

export function AdminPage() {
  const { t } = useTranslation();
  const user = useAppStore((s) => s.user);
  const [tab, setTab] = useState<Tab>("stats");

  if (user && !user.is_admin) return <Navigate to="/sites" replace />;

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t("admin.title")}</h1>
        <Badge kind="accent">admin</Badge>
      </div>

      <Segmented<Tab>
        value={tab}
        onChange={setTab}
        options={[
          { value: "stats", label: t("admin.tabs.stats") },
          { value: "users", label: t("admin.tabs.users") },
          { value: "tariffs", label: t("admin.tabs.tariffs") },
          { value: "domains", label: t("admin.tabs.domains") },
          { value: "zone", label: t("admin.tabs.zone") },
          { value: "about", label: t("admin.tabs.about") },
        ]}
      />

      {tab === "stats" ? <StatsTab /> : null}
      {tab === "users" ? <UsersTab /> : null}
      {tab === "tariffs" ? <TariffsTab /> : null}
      {tab === "domains" ? <DomainsTab /> : null}
      {tab === "zone" ? <ZoneTab /> : null}
      {tab === "about" ? <AboutTab /> : null}
    </div>
  );
}

function StatsTab() {
  const { t } = useTranslation();
  const toastError = useAppStore((s) => s.toastError);
  const [stats, setStats] = useState<AdminStats | null>(null);

  useEffect(() => {
    adminApi.stats().then(setStats).catch(toastError);
  }, [toastError]);

  if (!stats) return <Loading />;

  const cells: [string, string | number][] = [
    [t("admin.stats.users"), stats.total_users],
    [t("admin.stats.sites"), stats.total_sites],
    [t("admin.stats.published"), stats.published_sites],
    [t("admin.stats.subscriptions"), stats.active_subscriptions],
    [t("admin.stats.revenue"), stats.revenue],
    [t("admin.stats.revenue30"), stats.revenue_last_30d],
    [t("admin.stats.payments"), stats.payments_confirmed],
  ];

  return (
    <div className="stat-grid">
      {cells.map(([label, value]) => (
        <div className="stat" key={label}>
          <div className="stat-value">{value}</div>
          <div className="stat-label">{label}</div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const toast = useAppStore((s) => s.toast);
  const toastError = useAppStore((s) => s.toastError);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ users: AdminUserListItem[]; total: number } | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [granting, setGranting] = useState(false);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [grantForm, setGrantForm] = useState<{
    tariff_id: string;
    duration: TariffDuration;
    sites_limit: string;
    comment: string;
  }>({ tariff_id: "", duration: "month", sites_limit: "5", comment: "" });

  const load = useCallback(async () => {
    try {
      setData(await adminApi.users({ search, page, per_page: 20 }));
    } catch (error) {
      toastError(error);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    adminApi.tariffs().then(setTariffs).catch(() => undefined);
  }, []);

  async function openUser(id: string): Promise<void> {
    try {
      setDetail(await adminApi.user(id));
    } catch (error) {
      toastError(error);
    }
  }

  async function grant(): Promise<void> {
    if (!detail) return;
    try {
      await adminApi.grantAccess(detail.user.id, {
        tariff_id: grantForm.tariff_id || undefined,
        duration: grantForm.duration,
        sites_limit: grantForm.tariff_id ? undefined : Number(grantForm.sites_limit),
        comment: grantForm.comment || undefined,
      });
      toast(t("admin.users.granted"), "success");
      setGranting(false);
      await openUser(detail.user.id);
      await load();
    } catch (error) {
      toastError(error);
    }
  }

  async function revoke(subscriptionId: string): Promise<void> {
    if (!detail) return;
    const confirmed = await showConfirm(t("admin.users.revoke") + "?");
    if (!confirmed) return;
    try {
      await adminApi.revokeAccess(detail.user.id, subscriptionId);
      toast(t("admin.users.revoked"), "success");
      await openUser(detail.user.id);
    } catch (error) {
      toastError(error);
    }
  }

  return (
    <>
      <Input value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder={t("admin.users.search")} />

      {!data ? <Skeletons /> : null}
      {data?.users.length === 0 ? <Empty icon="🔍" title={t("admin.users.empty")} /> : null}

      <div className="list">
        {data?.users.map((item) => (
          <div key={item.id} className="card clickable" onClick={() => void openUser(item.id)}>
            <div className="card-row">
              <div className="grow">
                <div className="card-title">
                  {item.username ? `@${item.username}` : `id ${item.telegram_id}`}
                </div>
                <div className="card-sub">
                  {item.sites_count} {t("admin.users.sites")} · {item.active_subscriptions}{" "}
                  {t("admin.users.subs")}
                </div>
              </div>
              {item.is_admin ? <Badge kind="accent">admin</Badge> : null}
              {item.is_blocked ? <Badge kind="danger">blocked</Badge> : null}
            </div>
          </div>
        ))}
      </div>

      {data && data.total > 20 ? (
        <div className="row">
          <Button size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            ←
          </Button>
          <span className="card-sub">
            {page} / {Math.ceil(data.total / 20)}
          </span>
          <Button
            size="sm"
            disabled={page >= Math.ceil(data.total / 20)}
            onClick={() => setPage((p) => p + 1)}
          >
            →
          </Button>
        </div>
      ) : null}

      <Sheet
        open={Boolean(detail)}
        title={detail?.user.username ? `@${detail.user.username}` : t("admin.tabs.users")}
        onClose={() => setDetail(null)}
      >
        {detail ? (
          <div className="list">
            <div className="card">
              <div className="card-sub">{t("admin.users.wallet")}</div>
              <div className="mono">{detail.user.wallet_address ?? "—"}</div>
              <div className="card-sub" style={{ marginTop: 8 }}>
                {t("admin.users.registered")}: {new Date(detail.user.created_at).toLocaleDateString()}
              </div>
            </div>

            <Button variant="primary" block onClick={() => setGranting(true)}>
              {t("admin.users.grant")}
            </Button>

            <div className="field-label">{t("subscriptions.title")}</div>
            {detail.subscriptions.length === 0 ? (
              <div className="card-sub">{t("subscriptions.empty")}</div>
            ) : null}
            {detail.subscriptions.map((sub) => (
              <div key={sub.id} className="card">
                <div className="card-row">
                  <div className="grow">
                    <div className="card-title">{sub.tariff?.name ?? t("subscriptions.trial")}</div>
                    <div className="card-sub">
                      {sub.is_forever
                        ? t("subscriptions.forever")
                        : sub.expires_at
                          ? new Date(sub.expires_at).toLocaleDateString()
                          : "—"}
                    </div>
                  </div>
                  <Badge kind={sub.status === "expired" ? "danger" : "success"}>
                    {t(`subscriptions.status.${sub.status}`)}
                  </Badge>
                </div>
                {sub.status !== "expired" ? (
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" variant="danger" onClick={() => void revoke(sub.id)}>
                      {t("admin.users.revoke")}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}

            <div className="field-label">{t("admin.users.payments")}</div>
            {detail.payments.length === 0 ? (
              <div className="card-sub">{t("admin.users.noPayments")}</div>
            ) : null}
            {detail.payments.map((payment) => (
              <div key={payment.id} className="card">
                <div className="card-row">
                  <div className="grow">
                    <div className="card-title">
                      <GramAmount value={payment.amount} size={14} />
                    </div>
                    <div className="card-sub">
                      {payment.purpose} · {new Date(payment.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge kind={payment.status === "confirmed" ? "success" : "warning"}>
                    {payment.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Sheet>

      <Sheet open={granting} title={t("admin.grant.title")} onClose={() => setGranting(false)}>
        <div className="list">
          <Field label={t("admin.grant.tariff")}>
            <Select
              value={grantForm.tariff_id}
              onChange={(value) => setGrantForm((f) => ({ ...f, tariff_id: value }))}
              options={[
                { value: "", label: t("admin.grant.manual") },
                ...tariffs.map((tariff) => ({
                  value: tariff.id,
                  label: `${tariff.name} · ${tariff.sites_limit}`,
                })),
              ]}
            />
          </Field>

          {!grantForm.tariff_id ? (
            <Field label={t("admin.grant.sitesLimit")}>
              <Input
                value={grantForm.sites_limit}
                onChange={(value) => setGrantForm((f) => ({ ...f, sites_limit: value }))}
                inputMode="numeric"
              />
            </Field>
          ) : null}

          <Field label={t("admin.grant.duration")}>
            <Select
              value={grantForm.duration}
              onChange={(value) => setGrantForm((f) => ({ ...f, duration: value }))}
              options={DURATIONS.map((duration) => ({
                value: duration,
                label: t(`tariffs.duration.${duration}`),
              }))}
            />
          </Field>

          <Field label={t("admin.grant.comment")}>
            <Input
              value={grantForm.comment}
              onChange={(value) => setGrantForm((f) => ({ ...f, comment: value }))}
            />
          </Field>

          <Button variant="primary" block onClick={() => void grant()}>
            {t("admin.grant.submit")}
          </Button>
        </div>
      </Sheet>
    </>
  );
}

function TariffsTab() {
  const { t } = useTranslation();
  const toast = useAppStore((s) => s.toast);
  const toastError = useAppStore((s) => s.toastError);

  const [tariffs, setTariffs] = useState<Tariff[] | null>(null);
  const [editing, setEditing] = useState<Partial<Tariff> | null>(null);

  const load = useCallback(async () => {
    try {
      setTariffs(await adminApi.tariffs());
    } catch (error) {
      toastError(error);
    }
  }, [toastError]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (!editing) return;
    try {
      if (editing.id) {
        await adminApi.updateTariff(editing.id, {
          name: editing.name,
          description: editing.description,
          sites_limit: editing.sites_limit,
          duration: editing.duration,
          price_ton: editing.price_ton,
          kind: editing.kind,
          is_active: editing.is_active,
        });
      } else {
        await adminApi.createTariff({
          name: editing.name ?? "",
          description: editing.description ?? undefined,
          sites_limit: Number(editing.sites_limit ?? 1),
          duration: (editing.duration ?? "month") as TariffDuration,
          price_ton: String(editing.price_ton ?? "0"),
          kind: (editing.kind ?? "base") as TariffKind,
          is_active: editing.is_active ?? true,
        });
      }
      toast(t("admin.tariffs.saved"), "success");
      setEditing(null);
      await load();
    } catch (error) {
      toastError(error);
    }
  }

  async function remove(tariff: Tariff): Promise<void> {
    const confirmed = await showConfirm(t("admin.tariffs.deleteConfirm", { name: tariff.name }));
    if (!confirmed) return;
    try {
      await adminApi.deleteTariff(tariff.id);
      await load();
    } catch (error) {
      toastError(error);
    }
  }

  if (!tariffs) return <Skeletons />;

  return (
    <>
      <Button
        variant="primary"
        block
        onClick={() =>
          setEditing({ name: "", sites_limit: 1, duration: "month", price_ton: "1", kind: "base", is_active: true })
        }
      >
        + {t("admin.tariffs.create")}
      </Button>

      <div className="list">
        {tariffs.map((tariff) => (
          <div key={tariff.id} className="card">
            <div className="card-row">
              <div className="grow">
                <div className="row" style={{ gap: 6 }}>
                  <span className="card-title">{tariff.name}</span>
                  {tariff.kind === "custom_code" ? (
                    <Badge kind="accent">{t("admin.tariffs.oneTime")}</Badge>
                  ) : null}
                </div>
                <div className="card-sub row" style={{ gap: 5 }}>
                  <GramAmount value={tariff.price_ton} size={13} />
                  {tariff.kind === "custom_code" ? null : (
                    <>
                      · {t(`tariffs.duration.${tariff.duration}`)} · {tariff.sites_limit}
                    </>
                  )}
                </div>
              </div>
              <Badge kind={tariff.is_active ? "success" : "default"}>
                {tariff.is_active ? t("admin.tariffs.active") : t("admin.tariffs.inactive")}
              </Badge>
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <Button size="sm" onClick={() => setEditing(tariff)}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="danger" onClick={() => void remove(tariff)}>
                {t("common.delete")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Sheet
        open={Boolean(editing)}
        title={editing?.id ? t("common.edit") : t("admin.tariffs.create")}
        onClose={() => setEditing(null)}
      >
        {editing ? (
          <div className="list">
            <Field label={t("admin.tariffs.name")}>
              <Input
                value={editing.name ?? ""}
                onChange={(value) => setEditing({ ...editing, name: value })}
              />
            </Field>
            <Field label={t("admin.tariffs.description")}>
              <Input
                value={editing.description ?? ""}
                onChange={(value) => setEditing({ ...editing, description: value })}
              />
            </Field>
            <Field label={t("admin.tariffs.price")}>
              <Input
                value={String(editing.price_ton ?? "")}
                onChange={(value) => setEditing({ ...editing, price_ton: value })}
                inputMode="decimal"
              />
            </Field>
            <Field label={t("admin.tariffs.sitesLimit")}>
              <Input
                value={String(editing.sites_limit ?? "")}
                onChange={(value) => setEditing({ ...editing, sites_limit: Number(value) || 1 })}
                inputMode="numeric"
              />
            </Field>
            <Field label={t("admin.tariffs.duration")}>
              <Select
                value={(editing.duration ?? "month") as TariffDuration}
                onChange={(value) => setEditing({ ...editing, duration: value })}
                options={DURATIONS.map((duration) => ({
                  value: duration,
                  label: t(`tariffs.duration.${duration}`),
                }))}
              />
            </Field>
            <Field label={t("admin.tariffs.kind")}>
              <Select
                value={(editing.kind ?? "base") as TariffKind}
                onChange={(value) => setEditing({ ...editing, kind: value })}
                options={KINDS.map((kind) => ({ value: kind, label: kind }))}
              />
            </Field>
            <Field label={t("admin.tariffs.active")}>
              <Select
                value={editing.is_active === false ? "0" : "1"}
                onChange={(value) => setEditing({ ...editing, is_active: value === "1" })}
                options={[
                  { value: "1", label: t("admin.tariffs.active") },
                  { value: "0", label: t("admin.tariffs.inactive") },
                ]}
              />
            </Field>

            <Button variant="primary" block onClick={() => void save()}>
              {t("common.save")}
            </Button>
          </div>
        ) : null}
      </Sheet>
    </>
  );
}

function ZoneTab() {
  const { t } = useTranslation();
  const toast = useAppStore((s) => s.toast);
  const toastError = useAppStore((s) => s.toastError);
  const payment = useTonPayment();

  const [zone, setZone] = useState<Zone | null>(null);
  const [form, setForm] = useState<{
    domain: string;
    dns_item_address: string;
    collection_address: string;
    mode: "proxy" | "sbt";
  }>({ domain: "", dns_item_address: "", collection_address: "", mode: "sbt" });

  const load = useCallback(async () => {
    try {
      const data = await adminApi.zone();
      setZone(data);
      setForm({
        domain: data.domain,
        dns_item_address: data.dns_item_address,
        collection_address: data.collection_address,
        mode: data.mode,
      });
    } catch (error) {
      toastError(error);
    }
  }, [toastError]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    try {
      setZone(await adminApi.updateZone(form));
      toast(t("common.saved"), "success");
    } catch (error) {
      toastError(error);
    }
  }

  async function deploy(): Promise<void> {
    try {
      const { transaction, collection_address } = await adminApi.deployZone();
      // адрес коллекции известен заранее — подставляем его в форму сразу,
      // чтобы после подписи не искать контракт в эксплорере
      if (collection_address) setForm((f) => ({ ...f, collection_address }));
      const done = await payment.pay(transaction, async () => ({ ok: true }));
      if (done) toast(t("admin.zone.deployed"), "success");
    } catch (error) {
      toastError(error);
    }
  }

  if (!zone) return <Skeletons count={1} />;

  return (
    <>
      <Notice>{t("admin.zone.hint")}</Notice>

      <div className="card">
        <div className="row-between">
          <div className="card-title">{t("admin.zone.status")}</div>
          <Badge kind={zone.configured ? "success" : "warning"}>
            {zone.configured ? t("admin.zone.ready") : t("admin.zone.notReady")}
          </Badge>
        </div>
      </div>

      <Field label={t("admin.zone.domain")} hint={t("admin.zone.domainHint")}>
        <Input
          value={form.domain}
          onChange={(value) => setForm((f) => ({ ...f, domain: value }))}
          placeholder="mysites.ton"
        />
      </Field>

      <Field label={t("admin.zone.dnsItem")} hint={t("admin.zone.dnsItemHint")}>
        <Input
          value={form.dns_item_address}
          onChange={(value) => setForm((f) => ({ ...f, dns_item_address: value }))}
          placeholder="0:…"
        />
      </Field>

      <Field label={t("admin.zone.mode")} hint={t("admin.zone.modeHint")}>
        <Segmented<"sbt" | "proxy">
          value={form.mode}
          onChange={(value) => setForm((f) => ({ ...f, mode: value }))}
          options={[
            { value: "sbt", label: t("admin.zone.modeSbt") },
            { value: "proxy", label: t("admin.zone.modeProxy") },
          ]}
        />
      </Field>
      {zone.configured && zone.mode !== form.mode ? (
        <Notice kind="danger">{t("admin.zone.modeLocked")}</Notice>
      ) : null}

      <Field label={t("admin.zone.collection")} hint={t("admin.zone.collectionHint")}>
        <Input
          value={form.collection_address}
          onChange={(value) => setForm((f) => ({ ...f, collection_address: value }))}
          placeholder="0:…"
        />
      </Field>

      <Button variant="primary" block onClick={() => void save()}>
        {t("common.save")}
      </Button>

      <Button
        block
        disabled={!zone.deployable}
        loading={payment.stage === "signing" || payment.stage === "confirming"}
        onClick={() => void deploy()}
      >
        🚀 {t("admin.zone.deploy")}
      </Button>
      <div className="card-sub">{t("admin.zone.deployHint")}</div>
    </>
  );
}

/** Блок «Об авторе проекта»: текст и ссылка, которые видит пользователь. */
function AboutTab() {
  const { t } = useTranslation();
  const toast = useAppStore((s) => s.toast);
  const toastError = useAppStore((s) => s.toastError);
  const [about, setAbout] = useState<About | null>(null);
  const [form, setForm] = useState<About>({ title: "", text: "", link_url: "", link_label: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .about()
      .then((value) => {
        setAbout(value);
        setForm(value);
      })
      .catch(toastError);
  }, [toastError]);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const saved = await adminApi.updateAbout(form);
      setAbout(saved);
      setForm(saved);
      toast(t("common.saved"), "success");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  }

  if (!about) return <Skeletons count={1} />;

  return (
    <>
      <Notice>{t("admin.about.hint")}</Notice>

      <Field label={t("admin.about.name")}>
        <Input value={form.title} onChange={(value) => setForm((f) => ({ ...f, title: value }))} />
      </Field>

      <Field label={t("admin.about.text")}>
        <Textarea value={form.text} onChange={(value) => setForm((f) => ({ ...f, text: value }))} />
      </Field>

      <Field label={t("admin.about.linkUrl")} hint={t("admin.about.linkHint")}>
        <Input
          value={form.link_url}
          onChange={(value) => setForm((f) => ({ ...f, link_url: value }))}
          placeholder="https://t.me/username"
          inputMode="url"
        />
      </Field>

      <Field label={t("admin.about.linkLabel")}>
        <Input
          value={form.link_label}
          onChange={(value) => setForm((f) => ({ ...f, link_label: value }))}
        />
      </Field>

      <Button variant="primary" block loading={saving} onClick={() => void save()}>
        {t("common.save")}
      </Button>
    </>
  );
}

function DomainsTab() {
  const { t } = useTranslation();
  const toastError = useAppStore((s) => s.toastError);
  const [domains, setDomains] = useState<AdminDomainItem[] | null>(null);

  useEffect(() => {
    adminApi.domains().then(setDomains).catch(toastError);
  }, [toastError]);

  if (!domains) return <Skeletons />;
  if (!domains.length) return <Empty icon="🌐" title={t("admin.domains.empty")} />;

  return (
    <div className="list">
      {domains.map((item) => (
        <div key={item.site_id} className="card">
          <div className="card-row">
            <div className="grow">
              <div className="card-title">{item.domain}</div>
              <div className="card-sub">
                {item.site_title} · {t("admin.domains.owner")}: id {item.telegram_id}
              </div>
            </div>
            <Badge kind={item.status === "published" ? "success" : "default"}>
              {t(`sites.status.${item.status}`)}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}
